import React, { useEffect, useState, useCallback, useRef } from 'react';
import useResizeNotifier from '../../hooks/useResizeNotifier';
import { observer } from 'mobx-react';
import moment from 'moment-timezone';
import Heatmap from '../../Components/Heatmap/Heatmap';
import './HeatmapWidget.css';
import Tooltip from '../../Components/Heatmap/Tooltip';
import { ReactComponent as ArrowLeft } from '../../assets/icons/arrow-left-s-line.svg';
import { getHeatmapData } from '../../api/heatmap.api';
import {
  ColorMetric,
  GroupingType,
  MarketIndex,
  SizeMetric,
  ThemeType,
  WidgetMessageType,
  WidgetNameSource,
} from '../../utils/type';
import { BaseWidgetProps, WidgetHeightMessage } from '../../types/common.type';

import Loading from '../../Components/Loading/Loading';
import { ReactComponent as ErrorIcon } from '../../assets/icons/error-state.svg';
import ErrorMessage from '../../Components/ErrorMessage/ErrorMessage';
import { errorMsg } from '../../constants/common.constant';
import { getCssVariable } from '../../utils/utils';
import Layout from '../../Components/Common/Layout/Layout';

export interface HeatmapWidgetProps extends BaseWidgetProps {
  index?: MarketIndex; //XAO (S&P/ASX ALL ORDINARIES), XFL (S&P/ASX 50), XTL (S&P/ASX 20), XTO (S&P/ASX 100), XJO (S&P/ASX 200), DJI (Dow Jones Industrial Average), NDX (Nasdaq 100)
  sizeBy?: SizeMetric; // value1, value2
  colorBy?: ColorMetric; // pc, nc, ls
  showColorLegend?: boolean;
  groupBy?: GroupingType; // Sector
  token?: string;
  height?: number; //pixel height of the heatmap
  onHeightChange?: (message: WidgetHeightMessage) => void;
}
const HeatmapWidget = (props: HeatmapWidgetProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  useResizeNotifier(
    containerRef,
    WidgetNameSource.Heatmap,
    props.onHeightChange,
  );
  const [isLoading, setIsLoading] = useState(true);
  const onLoadingChangeRef = useRef(props.onLoadingChange);
  onLoadingChangeRef.current = props.onLoadingChange;
  useEffect(() => {
    onLoadingChangeRef.current?.(isLoading);
  }, [isLoading]);
  const [isEmptyData, setIsEmptyData] = useState(false);
  const msgItem = errorMsg.find((obj) => obj.key === 'heatmap');

  const [showBackButton, setShowBackButton] = useState<boolean>(false);
  const [showSubitem, setShowSubitem] = useState<boolean>(false);
  const [heatmapDataRaw, setHeatmapDataRaw] = useState<any[]>([]);
  const [heatmapData, setHeatmapData] = useState<any[]>([]);
  const [prevHeatmapData, setPrevHeatmapData] = useState<any[]>([]);
  const [heatmapDataLv1, setHeatmapDataLv1] = useState<any[]>([]);
  const [updateTime, setUpdateTime] = useState<number>(0);
  const [tooltip, setTooltip] = useState<any>({
    content: '',
    visible: false,
    x: 0,
    y: 0,
    ric: '',
  });
  // Debounce timer ref
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced setTooltip function
  const debouncedSetTooltip = useCallback(
    (newTooltip: any, delay: number = 5) => {
      // Clear existing timeout
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }

      // Set new timeout
      tooltipTimeoutRef.current = setTimeout(() => {
        setTooltip(newTooltip);
      }, delay);
    },
    [],
  );

  useEffect(() => {
    window.addEventListener('resize', () => {
      setUpdateTime(Date.now());
    });
    return () => {
      window.removeEventListener('resize', () => {
        setUpdateTime(Date.now());
      });
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setUpdateTime(Date.now());
    }, 2000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setShowBackButton(false);
    setHeatmapData([]);
    setHeatmapDataLv1([]);
    setPrevHeatmapData([]);
    setUpdateTime(Date.now());
    setIsEmptyData(false);
    setIsLoading(true);
    getHeatmapData(props.index || undefined, props.token || '')
      .then((data) => {
        if (data.length === 0) {
          setIsEmptyData(true);
          props.onNoData?.({
            type: WidgetMessageType.NoData,
            source: 'HeatmapWidget',
            data: null,
          });
        }
        setHeatmapDataRaw(data);
      })
      .catch((err) => {
        props.onError?.({
          message: err.message,
          type: err.name + '_' + err.code,
        });
        setIsEmptyData(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [props.index, props.groupBy]);

  useEffect(() => {
    if (heatmapDataRaw.length > 0) {
      chgHeatMapData(heatmapDataRaw);
    }
  }, [heatmapDataRaw]);

  const textInvert = getCssVariable('--text-invert');
  const textPrimary = getCssVariable('--text-primary');

  // Initial state for reset functionality
  const initialHeatmapColor = [
    {
      color: getCssVariable('--heatmap-up-6'),
      fontColor: textInvert,
      min: 5,
      max: 9999.0,
      label: '+5%',
    },
    {
      color: getCssVariable('--heatmap-up-5'),
      fontColor: textInvert,
      min: 4,
      max: 5,
      label: '+4%',
    },
    {
      color: getCssVariable('--heatmap-up-4'),
      fontColor: textInvert,
      min: 3,
      max: 4,
      label: '+3%',
    },
    {
      color: getCssVariable('--heatmap-up-3'),
      fontColor: textInvert,
      min: 2,
      max: 3,
      label: '+2%',
    },
    {
      color: getCssVariable('--heatmap-up-2'),
      fontColor: textInvert,
      min: 1,
      max: 2,
      label: '+1%',
    },
    {
      color: getCssVariable('--heatmap-up-1'),
      fontColor: textInvert,
      min: 0.5,
      max: 1,
      label: '+0.5%',
    },
    {
      color: getCssVariable('--heatmap-no-chg'),
      fontColor: textPrimary,
      min: -0.5,
      max: 0.5,
      label: '0%',
    },
    {
      color: getCssVariable('--heatmap-down-1'),
      fontColor: textInvert,
      min: -1,
      max: -0.5,
      label: '-0.5%',
    },
    {
      color: getCssVariable('--heatmap-down-2'),
      fontColor: textInvert,
      min: -2.0,
      max: -1.0,
      label: '-1%',
    },
    {
      color: getCssVariable('--heatmap-down-3'),
      fontColor: textInvert,
      min: -3.0,
      max: -2.0,
      label: '-2%',
    },
    {
      color: getCssVariable('--heatmap-down-4'),
      fontColor: textInvert,
      min: -4.0,
      max: -3.0,
      label: '-3%',
    },
    {
      color: getCssVariable('--heatmap-down-5'),
      fontColor: textInvert,
      min: -5.0,
      max: -4.0,
      label: '-4%',
    },
    {
      color: getCssVariable('--heatmap-down-6'),
      fontColor: textInvert,
      min: -9999.0,
      max: -5.0,
      label: '-5%',
    },
  ];

  const initialHeatmapColor2 = [
    {
      color: getCssVariable('--heatmap-range-9'),
      fontColor: textInvert,
      min: 200,
      max: 9999999.0,
      label: '200.00+',
    },
    {
      color: getCssVariable('--heatmap-range-8'),
      fontColor: textInvert,
      min: 100,
      max: 200,
      label: '100.00',
    },
    {
      color: getCssVariable('--heatmap-range-7'),
      fontColor: textInvert,
      min: 50,
      max: 100,
      label: '50.00',
    },
    {
      color: getCssVariable('--heatmap-range-6'),
      fontColor: textInvert,
      min: 30,
      max: 50,
      label: '30.00',
    },
    {
      color: getCssVariable('--heatmap-range-5'),
      fontColor: textInvert,
      min: 20,
      max: 30,
      label: '20.00',
    },
    {
      color: getCssVariable('--heatmap-range-4'),
      fontColor: textInvert,
      min: 10,
      max: 20,
      label: '10.00',
    },
    {
      color: getCssVariable('--heatmap-range-3'),
      fontColor: textInvert,
      min: 5,
      max: 10,
      label: '5.00',
    },
    {
      color: getCssVariable('--heatmap-range-2'),
      fontColor: getCssVariable('--heatmap-range-9'),
      min: 2,
      max: 5,
      label: '2.00',
    },
    {
      color: getCssVariable('--heatmap-range-1'),
      fontColor: getCssVariable('--heatmap-range-9'),
      min: -9999,
      max: 2,
      label: '1.00',
    },
  ];

  // Initial state for reset functionality
  const initialHeatmapColor3 = [
    {
      color: getCssVariable('--heatmap-up-6'),
      fontColor: textInvert,
      min: 5,
      max: 9999.0,
      label: '+5',
    },
    {
      color: getCssVariable('--heatmap-up-5'),
      fontColor: textInvert,
      min: 4,
      max: 5,
      label: '+4',
    },
    {
      color: getCssVariable('--heatmap-up-4'),
      fontColor: textInvert,
      min: 3,
      max: 4,
      label: '+3',
    },
    {
      color: getCssVariable('--heatmap-up-3'),
      fontColor: textInvert,
      min: 2,
      max: 3,
      label: '+2',
    },
    {
      color: getCssVariable('--heatmap-up-2'),
      fontColor: textInvert,
      min: 1,
      max: 2,
      label: '+1',
    },
    {
      color: getCssVariable('--heatmap-up-1'),
      fontColor: textInvert,
      min: 0.5,
      max: 1,
      label: '+0.5',
    },
    {
      color: getCssVariable('--heatmap-no-chg'),
      fontColor: textPrimary,
      min: -0.5,
      max: 0.5,
      label: '0',
    },
    {
      color: getCssVariable('--heatmap-down-1'),
      fontColor: textInvert,
      min: -1,
      max: -0.5,
      label: '-0.5',
    },
    {
      color: getCssVariable('--heatmap-down-2'),
      fontColor: textInvert,
      min: -2.0,
      max: -1.0,
      label: '-1',
    },
    {
      color: getCssVariable('--heatmap-down-3'),
      fontColor: textInvert,
      min: -3.0,
      max: -2.0,
      label: '-2',
    },
    {
      color: getCssVariable('--heatmap-down-4'),
      fontColor: textInvert,
      min: -4.0,
      max: -3.0,
      label: '-3',
    },
    {
      color: getCssVariable('--heatmap-down-5'),
      fontColor: textInvert,
      min: -5.0,
      max: -4.0,
      label: '-4',
    },
    {
      color: getCssVariable('--heatmap-down-6'),
      fontColor: textInvert,
      min: -9999.0,
      max: -5.0,
      label: '-5',
    },
  ];

  const [heatmapColor, setHeatmapColor] = useState<
    {
      color: string;
      fontColor: string;
      min: number;
      max: number;
      label: string;
    }[]
  >(initialHeatmapColor);

  useEffect(() => {
    if (props.colorBy === ColorMetric.PercentageChange) {
      setHeatmapColor(initialHeatmapColor);
    } else if (props.colorBy === ColorMetric.LastSale) {
      setHeatmapColor(initialHeatmapColor2);
    } else if (props.colorBy === ColorMetric.NetChange) {
      setHeatmapColor(initialHeatmapColor3);
    } else {
      setHeatmapColor(initialHeatmapColor);
    }
  }, [props.colorBy]);

  useEffect(() => {
    if (heatmapData.length > 0) {
      const sortedData = swapValueFields(heatmapData);
      setHeatmapData(sortedData);
    }
  }, [props.sizeBy]);

  // Function to swap between value and value2
  const swapValueFields = (data: any[]) => {
    // Update the grouped data with the new value field
    let updatedGroupedData = data.map((category) => {
      //console.log('category', category);
      const newCategory = { ...category };
      newCategory.value = category[props.sizeBy || 'value'];
      //console.log(category.name, 'newCategory.value',newCategory.value);

      return newCategory;
    });
    //updatedGroupedData.sort((a: any, b: any) => b.value - a.value).map(category => {
    //    console.log(category.name, 'props.sizeby',category[props.sizeby || 'value']);

    //});

    // Sort by the new value field
    const sortedData = updatedGroupedData.sort(
      (a: any, b: any) => b.value - a.value,
    );
    //console.log('sortedData', sortedData);
    return sortedData;
  };

  // Function to group data by categoryid
  const groupDataByCategory = (data: any[]) => {
    //return []
    const grouped = data.reduce((acc, item) => {
      const categoryId = item.categoryid;

      if (!acc[categoryId]) {
        acc[categoryId] = {
          name: item.category,
          //label: item.category,
          categoryid: categoryId,
          category: item.category,
          subcategoryid: item.subcategoryid,
          subcategory: item.subcategory,
          ls: 0,
          nc: 0,
          pc: 0,
          value: 0,
          value1: 0,
          value2: 0,
          volume: 0,
          turnover: 0,
          mktcap: 0,
          subitem: [],
        };
      }
      acc[categoryId].subitem.push(item);
      acc[categoryId].nc += item.nc || 0;
      acc[categoryId].pc += item.pc || 0;
      acc[categoryId].ls += item.ls || 0;
      acc[categoryId].volume += item.volume || 0;
      acc[categoryId].turnover += item.turnover || 0;
      acc[categoryId].mktcap += item.mktcap || 0;

      acc[categoryId].value += item[props.sizeBy || 'value'] || 0;

      return acc;
    }, {});

    // Convert to array and sort by total value
    return Object.values(grouped).sort((a: any, b: any) => b.value - a.value);
  };
  // Function to group data by categoryid
  const groupDataBySubCategory = (data: any[]) => {
    const grouped = data.reduce((acc, item) => {
      const subcategoryId = item.subcategoryid;
      if (!acc[subcategoryId]) {
        acc[subcategoryId] = {
          name: item.subcategory,
          //label: item.category,
          categoryid: item.categoryid,
          category: item.category,
          subcategoryid: subcategoryId,
          subcategory: item.subcategory,
          ls: 0,
          nc: 0,
          pc: 0,
          value: 0,
          value1: 0,
          value2: 0,
          subitem: [],
        };
      }
      acc[subcategoryId].subitem.push(item);
      acc[subcategoryId].value += item.value || 0;
      acc[subcategoryId].value1 += item.value1 || 0;
      acc[subcategoryId].value2 += item.value2 || 0;
      return acc;
    }, {});

    // Calculate aggregated values for each category
    Object.values(grouped).forEach((subcategory: any) => {
      if (subcategory.subitem.length > 0) {
        // Calculate average percentage change
        const totalPc = subcategory.subitem.reduce(
          (sum: number, item: any) => sum + (item.pc || 0),
          0,
        );
        subcategory.pc = totalPc / subcategory.subitem.length;

        // Calculate average last price
        const totalLs = subcategory.subitem.reduce(
          (sum: number, item: any) => sum + (item.ls || 0),
          0,
        );
        subcategory.ls = totalLs / subcategory.subitem.length;

        // Calculate total net change
        const totalNc = subcategory.subitem.reduce(
          (sum: number, item: any) => sum + (item.nc || 0),
          0,
        );
        subcategory.nc = totalNc;
      }
    });

    // Convert to array and sort by total value
    return Object.values(grouped).sort((a: any, b: any) => b.value - a.value);
  };
  const handleHeatmapColorChange = (color: string, index: number) => {
    setTimeout(() => {
      const newColors = [...heatmapColor];
      newColors[index].color = '#' + color;
      setHeatmapColor(newColors);
    }, 100);
  };

  const handleReset = () => {
    setHeatmapColor([...initialHeatmapColor]);
    setShowSubitem(false);
  };

  const resetHeatmapData = () => {
    if (props.groupBy === 'sector') {
      setHeatmapData(heatmapDataLv1);
      setShowSubitem(true);
      setShowBackButton(false);
    } else {
      setHeatmapData(swapValueFields(heatmapDataRaw));
    }
  };

  const chgHeatMapData = async (data: any[]) => {
    //clear tooltip
    setTooltip({
      content: '',
      visible: false,
      x: 0,
      y: 0,
      ric: '',
    });
    //setHeatmapData(data.sort((a, b) =>b.value - a.value).slice(0, MaxCells));
    if (data.length > 0) {
      if (data[0].name === data[0].subcategory) setShowSubitem(true);
      else setShowSubitem(false);
    } else {
      setShowSubitem(false);
    }
    let groupedData = heatmapDataRaw;
    if (props.groupBy === 'sector') {
      if (prevHeatmapData.length > 0) {
        groupedData = swapValueFields(data);
        setPrevHeatmapData(heatmapDataLv1);
        setShowSubitem(false);
        setShowBackButton(true);
      } else {
        groupedData = await groupDataByCategory(data);
        //swapValueFields for subitem
        groupedData.forEach(async (item) => {
          if (item.subitem && item.subitem.length > 0) {
            item.subitem = swapValueFields(item.subitem);
          }
        });
        setShowSubitem(true);
        setShowBackButton(false);
        setHeatmapDataLv1(groupedData);
        setPrevHeatmapData(groupedData);
      }
      setHeatmapData(groupedData);
    } else {
      setPrevHeatmapData(swapValueFields(groupedData));
      setHeatmapData(swapValueFields(data));
      setShowSubitem(false);
      setShowBackButton(false);
    }
  };

  // Default tooltip template
  const defaultTemplate = `{{ticker}}
{{company}}
{{industry}}

Last: {{lastPrice}}
NetChg: {{netChange}}
PerChg: {{percentageChange}}
Turnover: {{turnover}}
Vol: {{volume}}
Mkt Cap: {{marketCap}}
Time: {{tm}}`;

  // Use provided template or default
  const template = defaultTemplate;
  // Tracks the ric currently shown in the tooltip, so a slow pricetime response
  // is dropped if the user has already moved to another cell / closed it.
  const currentRicRef = useRef<string | null>(null);

  // Build the tooltip text directly from a heatmap data item.
  const buildTooltipContent = (item: any) => {
    let result = template;
    // Prepare data values
    const data = {
      ticker: item.name || '-',
      company: item.label || '-',
      industry: item.category || '-',
      lastPrice: item.rt_price
        ? Number(item.rt_price).toLocaleString(undefined, {
            minimumFractionDigits: item.dp,
            maximumFractionDigits: item.dp,
          })
        : '-',
      netChange: isFinite(Number(item.nc))
        ? (Number(item.nc) > 0 ? '+' : '') +
          Number(item.nc).toLocaleString(undefined, {
            minimumFractionDigits: item.dp,
            maximumFractionDigits: item.dp,
          })
        : '-',
      percentageChange: isFinite(Number(item.pc))
        ? (Number(item.pc) > 0 ? '+' : '') + Number(item.pc).toFixed(2) + '%'
        : '-',
      turnover: item.turnover ? Number(item.turnover).toLocaleString() : '-',
      volume: item.volume ? Number(item.volume).toLocaleString() : '-',
      marketCap: item.mktcap ? Number(item.mktcap) : '-',
      tm: item.rt_price_time
        ? moment(
            item.rt_price_date + ' ' + item.rt_price_time,
            'YYYY-MM-DD HH:mm:ss',
          ).format('HH:mm')
        : '-',
    };
    // Replace template variables
    Object.entries(data).forEach(([key, value]) => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      result = result.replace(regex, String(value));
    });

    // Clean up empty lines and extra spacing
    result = result.replace(/\n\s*\n\s*\n/g, '\n\n');
    return result;
  };

  const tooltipCallback = async (newTooltip: any) => {
    //check if tooltip.x,tooltip.y is same as last tooltip.x,tooltip.y, if same, return
    if (tooltip.show == true && newTooltip.show == false) {
      // For hiding tooltip, update immediately without debounce
      currentRicRef.current = null;
      debouncedSetTooltip({ ...newTooltip, show: false });
      return;
    }
    if (newTooltip.ric === '') {
      return;
    }
    //find item by tooltip.ric, from heatmapDataRaw
    const item = heatmapDataRaw.find(
      (item: any) => item.name === newTooltip.ric,
    );
    if (!item) {
      return;
    }

    // Calculate position relative to body
    const elementMaxWidth = 200;
    const elementMaxHeight = 240;
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Convert pageX/pageY to viewport-relative, then clamp, then convert back
    let tooltipX = newTooltip.x - scrollX; // viewport-relative x
    let tooltipY = newTooltip.y - scrollY; // viewport-relative y

    // If tooltip overflows right, flip to left of cursor
    if (tooltipX + elementMaxWidth > viewportWidth) {
      tooltipX = tooltipX - elementMaxWidth;
    }
    // If tooltip overflows bottom, flip above cursor
    if (tooltipY + elementMaxHeight > viewportHeight) {
      tooltipY = tooltipY - elementMaxHeight;
    }

    // Clamp to ensure tooltip stays within viewport (never negative)
    tooltipX = Math.max(0, Math.min(tooltipX, viewportWidth - elementMaxWidth));
    tooltipY = Math.max(
      0,
      Math.min(tooltipY, viewportHeight - elementMaxHeight),
    );

    // Convert back to page coordinates
    newTooltip.x = tooltipX + scrollX;
    newTooltip.y = tooltipY + scrollY;

    // Build the tooltip straight from the heatmap item data.
    currentRicRef.current = newTooltip.ric;
    debouncedSetTooltip({
      ...newTooltip,
      content: buildTooltipContent(item),
    });
  };

  return (
    <Layout theme={props?.theme || ThemeType.Direct}>
      <div
        className="heatmap-container"
        ref={containerRef}
        style={{ height: props.height ? props.height + 'px' : '500px' }}
      >
        {showBackButton && (
          <div className="heatmapboxtitletext">
            <div>{heatmapData.length > 0 ? heatmapData[0].category : ''} </div>
            <div
              style={{ cursor: 'pointer' }}
              onClick={() => {
                resetHeatmapData();
              }}
            >
              <ArrowLeft /> Back to all Sectors
            </div>
          </div>
        )}
        {isLoading && !props.onLoadingChange ? (
          <Loading />
        ) : !isLoading && isEmptyData ? (
          <ErrorMessage msg={msgItem} />
        ) : (
          <div className="chart-wrapper">
            {heatmapData.length > 0 && (
              <Heatmap
                page="mini"
                updateTime={updateTime}
                data={heatmapData}
                isGrouped={true}
                heatmapColor={heatmapColor}
                colorBy={props.colorBy || 'pc'}
                showSubitem={showSubitem}
                chgHeatMapData={chgHeatMapData}
                resetHeatmapData={resetHeatmapData}
                tooltipCallback={tooltipCallback}
              />
            )}
            {props.showColorLegend && heatmapData.length > 0 && (
              <div className="color-range-footer-container">
                {/*<div className="color-range-footer" style={{ background: 'linear-gradient(to right, ' + heatmapColor.reverse().map((item) => item.color).join(', ') + ')' }}>*/}
                <div className="color-range-footer">
                  {heatmapColor.map((item, index) => (
                    <div
                      key={'color-range-footer' + index}
                      className="color-range-footer-item-container"
                    >
                      <div
                        className="color-range-footer-item-color"
                        style={{
                          backgroundColor: item.color,
                          color: item.fontColor,
                        }}
                      ></div>
                      <div className="color-range-footer-item-text">
                        {item.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {/* horizontal color range footer */}
        {props.showColorLegend && heatmapData.length > 0 && (
          <div className="color-range-horizontal-footer-container">
            {/*<div className="color-range-horizontal-footer" style={{ background: 'linear-gradient(to right, ' + heatmapColor.reverse().map((item) => item.color).join(', ') + ')' }}>*/}
            <div className="color-range-horizontal-footer">
              {heatmapColor.map((item, index) => (
                <div
                  key={'color-range-horizontal-footer' + index}
                  className="color-range-horizontal-footer-item-container"
                >
                  <div
                    className="color-range-horizontal-footer-item-color"
                    style={{
                      backgroundColor: item.color,
                      color: item.fontColor,
                    }}
                  ></div>
                  <div className="color-range-horizontal-footer-item-text">
                    {item.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <Tooltip
          content={tooltip.content}
          visible={tooltip.show}
          x={tooltip.x}
          y={tooltip.y}
        />
      </div>
    </Layout>
  );
};

export default observer(HeatmapWidget);
